import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, QrCode, Copy, RefreshCw, Download, Lock,
  Shield, ChefHat, Wine, Sparkles, UserCog, Printer, Share2, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = {
  service: { label: "Service", icon: Users, color: "text-blue-500" },
  kitchen: { label: "Kueche", icon: ChefHat, color: "text-orange-500" },
  bar: { label: "Bar", icon: Wine, color: "text-purple-500" },
  cleaning: { label: "Reinigung", icon: Sparkles, color: "text-green-500" },
  management: { label: "Management", icon: UserCog, color: "text-yellow-500" },
};

const getRoleInfo = (roleValue) => ROLES[roleValue] || ROLES.service;

export const MitarbeiterZugang = () => {
  const navigate = useNavigate();
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [loginCard, setLoginCard] = useState(null);
  const qrImageRef = useRef(null);

  useEffect(() => { loadMitarbeiter(); }, []);

  const loadMitarbeiter = async () => {
    try {
      // Holt aus der Personal-Tabelle (mitarbeiter), nicht aus employees
      const res = await axios.get(`${API}/mitarbeiter`, { withCredentials: true });
      setMitarbeiter(res.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async (ma) => {
    if (!ma.employee_id) {
      toast.error("Kein Login-Zugang vorhanden. Mitarbeiter in Personal bearbeiten.");
      return;
    }
    try {
      const res = await axios.post(`${API}/employees/${ma.employee_id}/reset-pin`, {}, { withCredentials: true });
      toast.success(`Neue PIN fuer ${ma.name}: ${res.data.new_pin}`);
      loadMitarbeiter();
    } catch (err) {
      toast.error("Fehler beim Zuruecksetzen");
    }
  };

  const showQRCode = async (ma) => {
    if (!ma.employee_id) {
      toast.error("Kein Login-Zugang vorhanden");
      return;
    }
    setSelectedEmployee(ma);
    setQrLoading(true);
    setShowQRDialog(true);
    setLoginCard(null);

    try {
      const res = await axios.get(`${API}/employees/${ma.employee_id}/login-card`, { withCredentials: true });
      setLoginCard(res.data);
    } catch (err) {
      // Fallback: nutze Daten aus der Mitarbeiter-Tabelle
      setLoginCard({
        employee_name: ma.name,
        login_code: ma.login_code,
        pin: ma.pin,
        login_url: `${window.location.origin}/mitarbeiter-login`
      });
    } finally {
      setQrLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert!`);
  };

  const shareLoginInfo = async () => {
    if (!loginCard) return;
    const shareText = `${loginCard.employee_name} - MargeVio Login\n\nCode: ${loginCard.login_code}\nPIN: ${loginCard.pin}\n\nLogin: ${loginCard.login_url || window.location.origin + '/mitarbeiter-login'}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Login fuer ${loginCard.employee_name}`, text: shareText }); }
      catch { copyToClipboard(shareText, "Login-Daten"); }
    } else {
      copyToClipboard(shareText, "Login-Daten");
    }
  };

  const downloadQRCode = async () => {
    if (!selectedEmployee?.employee_id) return;
    try {
      const response = await axios.get(`${API}/employees/${selectedEmployee.employee_id}/qr-code`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${selectedEmployee.login_code || selectedEmployee.name}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("QR-Code heruntergeladen");
    } catch (err) {
      toast.error("Fehler beim Download");
    }
  };

  const printLoginCard = () => {
    if (!loginCard || !selectedEmployee) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Popup-Blocker aktiv'); return; }
    const doc = printWindow.document;
    doc.open();
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = `Login-Karte - ${loginCard.employee_name}`;
    const style = doc.createElement('style');
    style.textContent = `body{font-family:system-ui,sans-serif;padding:20px}.card{border:2px solid #333;border-radius:12px;padding:24px;max-width:350px;margin:0 auto}.header{text-align:center;border-bottom:1px solid #ddd;padding-bottom:12px;margin-bottom:16px}.header h1{margin:0;font-size:18px;color:#7c3aed}.qr{text-align:center;margin:16px 0}.qr img{width:150px;height:150px}.info{background:#f8f8f8;border-radius:8px;padding:12px}.info-row{display:flex;justify-content:space-between;margin:8px 0}.info-label{color:#666;font-size:12px}.info-value{font-weight:bold;font-size:14px;font-family:monospace}.footer{text-align:center;margin-top:16px;font-size:10px;color:#999}`;
    head.appendChild(title);
    head.appendChild(style);
    const body = doc.createElement('body');
    const card = doc.createElement('div');
    card.className = 'card';
    card.innerHTML = `<div class="header"><h1>MargeVio</h1></div>`;
    if (selectedEmployee.employee_id) {
      const qrDiv = doc.createElement('div');
      qrDiv.className = 'qr';
      const qrImg = doc.createElement('img');
      qrImg.src = `${API}/employees/${selectedEmployee.employee_id}/qr-code`;
      qrDiv.appendChild(qrImg);
      card.appendChild(qrDiv);
    }
    const info = doc.createElement('div');
    info.className = 'info';
    const rows = [['Name:', loginCard.employee_name], ['Code:', loginCard.login_code], ['PIN:', loginCard.pin]];
    rows.forEach(([l, v]) => {
      const row = doc.createElement('div');
      row.className = 'info-row';
      row.innerHTML = `<span class="info-label">${l}</span><span class="info-value">${v || ''}</span>`;
      info.appendChild(row);
    });
    card.appendChild(info);
    const footer = doc.createElement('div');
    footer.className = 'footer';
    footer.textContent = 'Code + PIN unter /mitarbeiter-login eingeben';
    card.appendChild(footer);
    body.appendChild(card);
    html.appendChild(head);
    html.appendChild(body);
    doc.appendChild(html);
    doc.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>;
  }

  const mitarbeiterMitZugang = mitarbeiter.filter(m => m.pin && m.login_code);
  const mitarbeiterOhneZugang = mitarbeiter.filter(m => !m.pin || !m.login_code);

  return (
    <div className="space-y-6" data-testid="mitarbeiter-zugang-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Mitarbeiter-Zugang</h1>
          <p className="text-slate-400 text-sm mt-1">Login-Daten und QR-Codes fuer Ihre Mitarbeiter aus der Personalverwaltung</p>
        </div>
        <Button onClick={() => navigate("/mitarbeiter")} variant="outline" className="gap-2 border-slate-600" data-testid="goto-personal-btn">
          <Users size={16} /> Personal verwalten <ArrowRight size={14} />
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-violet-900/20 border border-violet-700/50 rounded-xl p-4 flex items-start gap-3">
        <Shield size={20} className="text-violet-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-violet-300 font-medium">Mitarbeiter werden in der Personalverwaltung angelegt</p>
          <p className="text-xs text-violet-400/70 mt-1">Jeder neue Mitarbeiter erhaelt automatisch einen Login-Code und eine PIN. Hier verwalten Sie QR-Codes und koennen PINs zuruecksetzen.</p>
        </div>
      </div>

      {/* MA mit Zugang */}
      {mitarbeiterMitZugang.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 rounded-xl border border-slate-700">
          <Users size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">Noch keine Mitarbeiter mit Login-Zugang</p>
          <p className="text-xs text-slate-500 mt-2">Legen Sie Mitarbeiter in der Personalverwaltung an — Login-Daten werden automatisch generiert.</p>
          <Button onClick={() => navigate("/mitarbeiter")} className="mt-4 bg-violet-600 hover:bg-violet-700 gap-2">
            <Users size={16} /> Zur Personalverwaltung
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mitarbeiterMitZugang.map((ma) => {
            const roleInfo = getRoleInfo(ma.position?.toLowerCase().includes("koch") || ma.position?.toLowerCase().includes("kuech") ? "kitchen"
              : ma.position?.toLowerCase().includes("kellner") || ma.position?.toLowerCase().includes("service") ? "service"
              : ma.position?.toLowerCase().includes("bar") ? "bar"
              : ma.position?.toLowerCase().includes("manager") || ma.position?.toLowerCase().includes("chef") ? "management"
              : "service");
            const RoleIcon = roleInfo.icon;
            return (
              <div key={ma.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors" data-testid={`ma-card-${ma.login_code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-slate-700 ${roleInfo.color}`}><RoleIcon size={20} /></div>
                    <div>
                      <h3 className="font-semibold text-white">{ma.name}</h3>
                      <span className="text-xs text-slate-400">{ma.position}</span>
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 bg-violet-900/30 text-violet-400 rounded flex items-center gap-1">
                    <Lock size={8} /> {ma.zugriff_module?.length || 0} Module
                  </span>
                </div>

                {/* Login Info */}
                <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-slate-400">Code:</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-white">{ma.login_code}</code>
                      <button onClick={() => copyToClipboard(ma.login_code, "Code")} className="text-slate-500 hover:text-white"><Copy size={12} /></button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">PIN:</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-white">{ma.pin}</code>
                      <button onClick={() => copyToClipboard(ma.pin, "PIN")} className="text-slate-500 hover:text-white"><Copy size={12} /></button>
                    </div>
                  </div>
                </div>

                {/* Modules */}
                {ma.zugriff_module && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {ma.zugriff_module.slice(0, 5).map(mod => (
                      <span key={mod} className="text-[9px] px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded capitalize">{mod}</span>
                    ))}
                    {ma.zugriff_module.length > 5 && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded">+{ma.zugriff_module.length - 5}</span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1 border-slate-600" onClick={() => showQRCode(ma)} data-testid={`qr-btn-${ma.login_code}`}>
                    <QrCode size={14} /> QR-Code
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1 border-slate-600" onClick={() => handleResetPin(ma)} data-testid={`reset-pin-${ma.login_code}`}>
                    <RefreshCw size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MA ohne Zugang */}
      {mitarbeiterOhneZugang.length > 0 && (
        <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-400 mb-2">Mitarbeiter ohne Login ({mitarbeiterOhneZugang.length})</h3>
          <p className="text-xs text-slate-500 mb-3">Diese Mitarbeiter wurden vor dem Login-System angelegt. Bearbeiten Sie sie in der Personalverwaltung, um Login-Daten zu generieren.</p>
          <div className="space-y-2">
            {mitarbeiterOhneZugang.map(ma => (
              <div key={ma.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                <span className="text-sm text-slate-300">{ma.name} <span className="text-xs text-slate-500">({ma.position})</span></span>
                <Button variant="ghost" size="sm" className="text-xs text-violet-400" onClick={() => navigate("/mitarbeiter")}>In Personal bearbeiten</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white"><QrCode size={20} className="text-violet-500" /> Login fuer {selectedEmployee?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Code + PIN eingeben oder QR-Code scannen</DialogDescription>
          </DialogHeader>
          {qrLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>
          ) : loginCard && (
            <div className="space-y-4 py-4">
              {selectedEmployee?.employee_id && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl border-2 border-slate-200 inline-block">
                    <img ref={qrImageRef} src={`${API}/employees/${selectedEmployee.employee_id}/qr-code`} alt="QR Code" className="w-48 h-48" data-testid="qr-code-image" />
                  </div>
                </div>
              )}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Code:</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono font-bold text-lg text-white">{loginCard.login_code}</code>
                    <button onClick={() => copyToClipboard(loginCard.login_code, "Code")} className="text-slate-500 hover:text-white"><Copy size={14} /></button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">PIN:</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono font-bold text-lg text-white">{loginCard.pin}</code>
                    <button onClick={() => copyToClipboard(loginCard.pin, "PIN")} className="text-slate-500 hover:text-white"><Copy size={14} /></button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 gap-2 border-slate-600" onClick={downloadQRCode} data-testid="download-qr-btn"><Download size={16} /> Download</Button>
                <Button variant="outline" className="flex-1 gap-2 border-slate-600" onClick={printLoginCard} data-testid="print-card-btn"><Printer size={16} /> Drucken</Button>
                <Button variant="outline" className="gap-2 border-slate-600" onClick={shareLoginInfo} data-testid="share-btn"><Share2 size={16} /></Button>
              </div>
              <p className="text-xs text-center text-slate-500">Login: {window.location.origin}/mitarbeiter-login</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MitarbeiterZugang;
